// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

/**
 * Centralised badge appearance/colour configuration — Shared-CSS-Reference v3.1 §19.
 *
 * Pages import these maps + `getBadgeProps` instead of defining their own local
 * colour constants. Colour semantics (v3.1 Rule 5): success = good, warning =
 * attention, danger = bad, informative = neutral, subtle = inactive, brand = primary.
 *
 * The maps below are Cloud-Adoption-specific (project/document/register
 * vocabularies). Display strings are never stored here — pages render the status
 * value itself (i18n keying of badge labels is Phase 3c / C61).
 */

export type BadgeColor = 'success' | 'danger' | 'warning' | 'informative' | 'subtle' | 'brand';
export type BadgeAppearance = 'filled' | 'tint' | 'outline';

export interface BadgeConfig {
  appearance: BadgeAppearance;
  color: BadgeColor;
}

// ---------------------------------------------------------------------------
// Document lifecycle status (draft → review → final)
// ---------------------------------------------------------------------------

export const DOC_STATUS_BADGE: Record<string, BadgeConfig> = {
  draft:  { appearance: 'filled', color: 'warning' },
  review: { appearance: 'filled', color: 'brand' },
  final:  { appearance: 'filled', color: 'success' },
};

// ---------------------------------------------------------------------------
// Project status
// ---------------------------------------------------------------------------

export const PROJECT_STATUS_BADGE: Record<string, BadgeConfig> = {
  active:   { appearance: 'filled', color: 'success' },
  complete: { appearance: 'tint',   color: 'informative' },
};

// ---------------------------------------------------------------------------
// CAF phase
// ---------------------------------------------------------------------------

export const CAF_PHASE_BADGE: Record<string, BadgeConfig> = {
  governance: { appearance: 'tint', color: 'informative' },
  strategy:   { appearance: 'tint', color: 'brand' },
  plan:       { appearance: 'tint', color: 'informative' },
  ready:      { appearance: 'tint', color: 'warning' },
  adopt:      { appearance: 'tint', color: 'success' },
  review:     { appearance: 'tint', color: 'informative' },
  complete:   { appearance: 'tint', color: 'success' },
  misc:       { appearance: 'tint', color: 'subtle' },
};

// ---------------------------------------------------------------------------
// Change-request status
// ---------------------------------------------------------------------------

export const CR_STATUS_BADGE: Record<string, BadgeConfig> = {
  approved: { appearance: 'filled', color: 'success' },
  proposed: { appearance: 'filled', color: 'informative' },
  deferred: { appearance: 'filled', color: 'warning' },
  rejected: { appearance: 'filled', color: 'danger' },
};

// ---------------------------------------------------------------------------
// Fact / assumption status
// ---------------------------------------------------------------------------

export const FACT_STATUS_BADGE: Record<string, BadgeConfig> = {
  confirmed: { appearance: 'filled', color: 'success' },
  assumed:   { appearance: 'filled', color: 'warning' },
  unknown:   { appearance: 'filled', color: 'danger' },
};

// ---------------------------------------------------------------------------
// UX-policy disposition
// ---------------------------------------------------------------------------

export const UX_DISPOSITION_BADGE: Record<string, BadgeConfig> = {
  deploy:  { appearance: 'filled', color: 'success' },
  defer:   { appearance: 'filled', color: 'warning' },
  exclude: { appearance: 'filled', color: 'danger' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get badge props for a status key, falling back to a neutral subtle badge. */
export function getBadgeProps(
  map: Record<string, BadgeConfig>,
  key: string,
  fallback: BadgeConfig = { appearance: 'tint', color: 'subtle' },
): BadgeConfig {
  return map[key] ?? fallback;
}

/** Continuous 0–10 score → colour (simulation quality scores). */
export function getScoreColor(score: number): BadgeColor {
  if (score >= 8) return 'success';
  if (score >= 6) return 'warning';
  return 'danger';
}

/** Risk score (likelihood × impact, 1–25) → colour. */
export function getRiskColor(score: number): BadgeColor {
  if (score >= 15) return 'danger';
  if (score >= 8) return 'warning';
  return 'success';
}

/** Completeness ratio (0–1) → colour. */
export function getCompletenessColor(ratio: number): BadgeColor {
  if (ratio >= 0.8) return 'success';
  if (ratio >= 0.5) return 'warning';
  return 'danger';
}
